import { useState } from 'react'

import { Activity } from '../interfaces/DealDto'
import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'
import { Rule, validateFields } from '../utils/validateForm'
import { fechaIsoToFull, fechaIsoToIso } from '../utils/utils'
import activityTypeTranslate from '../utils/activityTypeTranslate'
import activityStatusTranslate, { transActivityStatus } from '../utils/activityStatusTranslate'
import Deal from '../interfaces/Deal'
import useDealActivityEdit from '../hooks/useDealActivityEdit'

import { ButtonActionShort, ButtonDelete } from './Buttons'
import { SelectField } from './SelectField'
import InputField from './InputField'
import ConfirmationModal from './ConfirmationModal'

type FormElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

// Muestra edita Actividades (Historial)  del Negocio
const DealActivity: React.FC<{ deal: Deal; onRefresh: () => void }> = ({ deal, onRefresh }) => {
  const iniActivity: Activity = {
    date: '',
    body: '',
    _id: '',
    deal: deal._id,
    profile: '',
    createdAt: '',
    updatedAt: '',
    __v: 0,
    sortDate: '',
    status: 'STATUS.SCHEDULED',
    type: '',
  }
  const activities = deal.body.activities
  const [showActivityEdit, setShowActivityEdit] = useState<boolean>(false)
  const [activity, setActivity] = useState<Activity | null>(iniActivity)
  const { activityEdit, errorActivityEdit, activityDelete } = useDealActivityEdit()
  const [modificandoSw, setModificandoSw] = useState<boolean>(false)
  // Errores de validacion de los input
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [selectedItemId, setSelectedItemId] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const handleInputChange = (e: React.ChangeEvent<FormElement>) => {
    const formatProps: FormatProps[] = []

    if (!activity) return
    handleInputChangeFormat<Activity>(e, activity, setActivity, formatProps)
  }
  /*
    Inserta o modifica una nueva actividad
    */
  const handleAceptarClick = async () => {
    if (!activity) return
    const fieldsToValidate = [
      { name: 'type', value: activity?.type, rule: Rule.REQUIRED },
      { name: 'date', value: activity?.date, rule: Rule.REQUIRED },
      { name: 'status', value: activity?.status, rule: Rule.REQUIRED },
    ]
    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return
    }

    if (activity?.type === 'TYPE.OTHER' && (!activity.body || activity.body.trim().length == 0)) {
      const otherErrors: { [key: string]: string } = {}
      otherErrors['body'] = 'Requerido'
      setErrors(otherErrors)

      return
    }
    setErrors({})

    const id = await activityEdit(activity, modificandoSw)

    if (id) {
      setShowActivityEdit(false)
      onRefresh()
    }
  }
  const handleAgregarClick = () => {
    setActivity(iniActivity)
    setShowActivityEdit(true)
    setModificandoSw(false)
  }
  // Click en una lina para modificar
  const handleRowClick = (key: string) => {
    setShowActivityEdit(true)
    const clickedActivities = activities.filter((a: Activity) => a._id === key)

    if (clickedActivities.length === 0) return // caso raro

    if (!activity) return
    const clickedAct = clickedActivities[0]
    setActivity({
      ...activity,
      type: clickedAct.type ?? 'TYPE.OTHER',
      body: clickedAct.body,
      date: fechaIsoToIso(clickedAct.date),
      status: clickedAct.status,
      _id: clickedAct._id,
    })
    setModificandoSw(true)
  }

  const handleCancelarClick = () => {
    setActivity(iniActivity)
    setShowActivityEdit(false)
  }
  const transActivityType = (type: string | undefined) => {
    if (!type) {
      type = 'TYPE.OTHER'
    }

    return activityTypeTranslate[type]
  }

  // Begin borra una actividad
  const handleDelete = async (key: string) => {
    setSelectedItemId(key) // Store the item ID in the state
    setIsModalOpen(true)
  }
  const handleConfirm = async () => {
    setIsModalOpen(false)
    // Perform delete action here
    const res = await activityDelete(selectedItemId)
    setSelectedItemId('')

    if (res) {
      onRefresh()
    }
  }
  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedItemId('')
  }
  // End borra una actividad

  return (
    <div>
      <div className="grid grid-cols-10 font-bold mb-4 gap-2">
        <div className="col-span-1">Actividad</div>
        <div className="col-span-3">Notas</div>
        <div className="col-span-2">Fecha agendada</div>
        <div className="col-span-2">Estado</div>
        <div className="col-span-2">
          <ButtonActionShort label="Agregar..." handleButtonClick={handleAgregarClick} />
        </div>
      </div>
      {(activities ?? []).map((field) => (
        <div
          key={field._id}
          className="grid grid-cols-10 gap-2 cursor-pointer"
          onClick={() => handleRowClick(field._id)}
        >
          <div className="col-span-1">{transActivityType(field.type)}</div>
          <div className="col-span-3">{field.body}</div>
          <div className="col-span-2">{fechaIsoToFull(field.date)}</div>
          <div className="col-span-2">{transActivityStatus(field.status)}</div>
          <ButtonDelete key={field._id} handleButtonClick={() => handleDelete(field._id)} />
        </div>
      ))}

      {showActivityEdit && (
        <>
          <div className="border-4 mt-12 p-2">
            <div className="grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-8 gap-4 mt-4">
              <div className="col-span-2">
                <SelectField
                  label="Actividad"
                  name="type"
                  value={activity?.type}
                  options={activityTypeTranslate}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
              </div>
              <div className="col-span-3">
                <InputField
                  label="Notas"
                  name="body"
                  value={activity?.body ?? ''}
                  type="string"
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
              </div>
              <div className="col-span-2">
                <InputField
                  label="Fecha Agendada"
                  name="date"
                  value={activity?.date ?? ''}
                  type="datetime-local"
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
              </div>
              <SelectField
                label="Estado"
                name="status"
                value={activity?.status}
                options={activityStatusTranslate}
                handleInputChange={handleInputChange}
                errors={errors}
              />
            </div>
          </div>
          <div className="flex justify-center mt-4 space-x-4">
            <ButtonActionShort label="Aceptar" handleButtonClick={handleAceptarClick} />
            <ButtonActionShort label="Cancelar" handleButtonClick={handleCancelarClick} />
          </div>
        </>
      )}
      {isModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro de eliminar esta actividad?"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      <div>{errorActivityEdit?.message}</div>
    </div>
  )
}
export default DealActivity
